<script>
  import { settings, modalState } from '../store.js';
  import { currentInnings, formatOvers, currentOverBalls, reqRate } from '../../scoring-logic.js';
  import { getMeta } from '../../store.js';

  export let match;

  $: inn = match ? currentInnings(match) : null;
  $: battingTeamLetter = inn ? inn.battingTeam : 'A';
  $: bowlingTeamLetter = inn ? (inn.battingTeam === 'A' ? 'B' : 'A') : 'B';
  $: battingTeam = match ? (battingTeamLetter === 'A' ? match.teamA : match.teamB) : null;
  $: bowlingTeam = match ? (bowlingTeamLetter === 'A' ? match.teamA : match.teamB) : null;
  $: batMeta = battingTeam ? getMeta(battingTeam.name) : { color: '#0a7d28', logo: null };
  $: bowlMeta = bowlingTeam ? getMeta(bowlingTeam.name) : { color: '#ef4444', logo: null };

  $: striker = inn ? inn.batsmen.find(b => b.name === inn.currentStriker) : null;
  $: nonStriker = inn ? inn.batsmen.find(b => b.name === inn.currentNonStriker) : null;
  $: bowler = inn ? inn.bowlers.find(b => b.name === inn.currentBowler) : null;

  $: target = (match && match.innings.length === 2) ? match.innings[0].totalRuns + 1 : (match?.challenge?.targetValue || null);
  $: challengeConfig = match?.mode === 'challenge' && match.challenge?.type !== 'super_over' ? match.challenge : null;
  $: need = target ? Math.max(0, target - inn.totalRuns) : 0;
  $: ballsLeft = (target || challengeConfig) ? Math.max(0, match.overs * 6 - inn.balls) : 0;

  $: challengeTargetText = (() => {
    if (!challengeConfig) return null;
    const c = challengeConfig;
    if (c.type === 'target_runs') {
      const needRuns = Math.max(0, c.targetValue - inn.totalRuns);
      return `NEED ${needRuns} RUNS`;
    }
    if (c.type === 'target_sixes') {
      const currentSixes = inn.batsmen.reduce((sum, b) => sum + b.sixes, 0);
      const needSixes = Math.max(0, c.targetValue - currentSixes);
      return `NEED ${needSixes} SIXES`;
    }
    if (c.type === 'target_wickets') {
      const needWkts = Math.max(0, c.targetValue - inn.wickets);
      return `NEED ${needWkts} WKTS`;
    }
    return null;
  })();

  $: overBalls = inn ? currentOverBalls(inn) : [];

  // Last Man Standing check
  $: isLMS = (() => {
    if (!match || !inn || !match.lms) return false;
    const battingPlayers = inn.battingTeam === 'A' ? match.teamA.players : match.teamB.players;
    const maxWkts = match.lms ? battingPlayers.length : battingPlayers.length - 1;
    return inn.wickets >= maxWkts - 1 && !inn.currentNonStriker;
  })();

  function ballPillClass(b) {
    if (b.type === 'wicket') return 'pill-wkt';
    if (b.type === 'wide') return 'pill-wd';
    if (b.type === 'nb') return 'pill-nb';
    if (b.runs === 0) return 'pill-dot';
    if (b.runs === 4) return 'pill-four';
    if (b.runs === 6) return 'pill-six';
    return 'pill-run';
  }

  function ballPillText(b) {
    if (b.type === 'wicket') return 'W';
    if (b.type === 'wide') return b.runs + 'wd';
    if (b.type === 'nb') return b.runs + 'nb';
    // the engine logs byes as type 'b' (not 'bye')
    if (b.type === 'b' || b.type === 'lb') return b.runs + b.type;
    return String(b.runs);
  }

  function handleBowlerClick() {
    if (!inn) return;
    // Mid-over bowler changes are legal (injury, blood on the ball) and the
    // engine already supports them — `endOver: false` just reassigns
    // inn.currentBowler without rotating the strike. The old
    // `overBalls.length === 0` gate locked scorers out of the feature entirely.
    $modalState = { type: 'selectBowler', payload: { endOver: false, midOver: overBalls.length > 0 } };
  }

  let _playerRegistry = null;
  let _registryRaw = null;
  $: registry = (() => {
    try {
      const raw = localStorage.getItem('cs_player_registry');
      if (raw !== _registryRaw) {
        _registryRaw = raw;
        _playerRegistry = raw ? JSON.parse(raw) : {};
      }
      return _playerRegistry || {};
    } catch (_) {
      return {};
    }
  })();

  function getPlayerPhoto(name) {
    if (!name) return null;
    const player = Object.values(registry).find(p => p.displayName === name || p.username === name);
    return player?.photo || null;
  }

  function openPlayerDetail(name) {
    if (!name) return;
    $modalState = { type: 'playerDetail', payload: { name } };
  }
</script>

{#if match && inn && battingTeam}
<div class="sb-pane">
  <!-- Row 1: Team Score + Overs -->
  <div class="sb-row1">
    <div class="sb-team">
      {#if batMeta.logo}
        <img src={batMeta.logo} alt={battingTeam.name} class="team-logo" />
      {:else}
        <div class="team-avatar-badge" style="background: linear-gradient(135deg, {batMeta.color}dd 0%, {batMeta.color}88 100%); border: 1px solid {batMeta.color};">
          {battingTeam.name.split(' ').map(w => w[0]).join('').toUpperCase().slice(0, 3)}
        </div>
      {/if}
      <div>
        <div class="team-name" style="color:{batMeta.color}">{battingTeam.name}</div>
        <div class="sb-total">
          {inn.totalRuns}<small> / {inn.wickets}</small>
        </div>
      </div>
    </div>
    <div class="sb-overs">
      <div class="label">OVERS</div>
      <div class="value">{formatOvers(inn.balls)} / {match.overs}</div>
      {#if challengeTargetText}
        <div class="sb-target">{challengeTargetText} OFF {ballsLeft}</div>
        <div class="sb-target-sub">TARGET: {challengeConfig.targetValue} {challengeConfig.type.split('_')[1].toUpperCase()}</div>
      {:else if target}
        <div class="sb-target">NEED {need} OFF {ballsLeft} • RRR {reqRate(need, ballsLeft)}</div>
        <div class="sb-target-sub">TARGET {target}</div>
      {/if}
    </div>
  </div>

  <!-- Row 2: Batsmen + Bowler Cards -->
  <div class="sb-row2">
    <!-- Batting Card -->
    <div class="sb-card">
      <div class="accent" style="background:{batMeta.color}"></div>
      {#if striker}
        <div class="sb-line" style="display:flex; align-items:center;">
          {#if getPlayerPhoto(striker.name)}
            <img src={getPlayerPhoto(striker.name)} alt="avatar" style="width:20px; height:20px; border-radius:50%; margin-right:6px; object-fit:cover;" />
          {/if}
          <!-- svelte-ignore a11y_click_events_have_key_events -->
          <!-- svelte-ignore a11y_no_static_element_interactions -->
          <span class="pname" style="color:{batMeta.color}; cursor:pointer;" on:click={() => openPlayerDetail(striker.name)}>▶ {striker.name}</span>
          <span class="pstats" style="margin-left:auto;">{striker.runs}* ({striker.balls})</span>
        </div>
      {:else}
        <div class="sb-line muted">Select striker...</div>
      {/if}
      {#if nonStriker}
        <div class="sb-line dim" style="display:flex; align-items:center;">
          {#if getPlayerPhoto(nonStriker.name)}
            <img src={getPlayerPhoto(nonStriker.name)} alt="avatar" style="width:20px; height:20px; border-radius:50%; margin-right:6px; object-fit:cover; opacity:0.8;" />
          {/if}
          <!-- svelte-ignore a11y_click_events_have_key_events -->
          <!-- svelte-ignore a11y_no_static_element_interactions -->
          <span class="pname" style="cursor:pointer;" on:click={() => openPlayerDetail(nonStriker.name)}>{nonStriker.name}</span>
          <span class="pstats" style="margin-left:auto;">{nonStriker.runs} ({nonStriker.balls})</span>
        </div>
      {:else if isLMS}
        <div class="sb-line lms">🛡 LAST MAN STANDS</div>
      {:else}
        <div class="sb-line muted dim">Select non-striker...</div>
      {/if}
    </div>

    <!-- Bowling Card -->
    <div class="sb-card">
      <div class="accent accent-r" style="background:{bowlMeta.color}"></div>
      {#if bowler}
        <!-- svelte-ignore a11y_click_events_have_key_events -->
        <!-- svelte-ignore a11y_no_static_element_interactions -->
        <div class="sb-line clickable" on:click={handleBowlerClick} style="display:flex; align-items:center;">
          {#if getPlayerPhoto(bowler.name)}
            <img src={getPlayerPhoto(bowler.name)} alt="avatar" style="width:20px; height:20px; border-radius:50%; margin-right:6px; object-fit:cover;" />
          {/if}
          <!-- svelte-ignore a11y_click_events_have_key_events -->
          <!-- svelte-ignore a11y_no_static_element_interactions -->
          <span class="pname" style="color:{bowlMeta.color}; cursor:pointer;" on:click|stopPropagation={() => openPlayerDetail(bowler.name)}>▶ {bowler.name}</span>
          <span class="pstats" style="margin-left:auto;">{bowler.wickets}-{bowler.runs} ({formatOvers(bowler.balls)})</span>
        </div>
      {:else}
        <div class="sb-line muted">Select bowler...</div>
      {/if}
      {#if $settings.showOverballStats !== false}
      <div class="over-strip">
        {#each overBalls as b, i (b.id || i)}
          <span class="pill {ballPillClass(b)}">{ballPillText(b)}</span>
        {/each}
        {#if overBalls.length === 0}
          <span class="pill pill-empty">—</span>
        {/if}
      </div>
      {/if}
    </div>
  </div>
</div>
{/if}

<style>
  .sb-pane {
    display: flex;
    flex-direction: column;
    gap: 6px;
    padding: 8px;
    background: linear-gradient(135deg, #111827 0%, #1a1a2e 100%);
    border-radius: 10px;
    border: 1px solid rgba(255,255,255,0.06);
  }
  .sb-row1 {
    display: flex;
    justify-content: space-between;
    align-items: center;
    gap: 8px;
  }
  .sb-team {
    display: flex;
    align-items: center;
    gap: 8px;
  }
  .team-logo {
    width: 30px;
    height: 30px;
    border-radius: 6px;
    object-fit: cover;
  }
  .team-dot {
    width: 28px;
    height: 28px;
    border-radius: 6px;
    flex-shrink: 0;
  }
  .team-avatar-badge {
    width: 28px;
    height: 28px;
    border-radius: 6px;
    flex-shrink: 0;
    display: flex;
    align-items: center;
    justify-content: center;
    color: #ffffff;
    font-weight: 900;
    font-size: 11px;
    font-family: Outfit, sans-serif;
    box-shadow: 0 2px 6px rgba(0,0,0,0.3);
    text-shadow: 0 1px 2px rgba(0,0,0,0.6);
  }
  .team-name {
    font-weight: 800;
    font-size: 12px;
    text-transform: uppercase;
    letter-spacing: 0.5px;
  }
  .sb-total {
    font-size: 28px;
    font-weight: 900;
    color: #fff;
    line-height: 1;
    font-family: ui-monospace, "SF Mono", monospace;
    font-variant-numeric: tabular-nums;
  }
  .sb-total small {
    font-size: 16px;
    opacity: 0.6;
    font-weight: 600;
  }
  .sb-overs {
    text-align: right;
  }
  .sb-overs .label {
    font-size: 9px;
    color: #9ca3af;
    font-weight: 700;
    letter-spacing: 1px;
  }
  .sb-overs .value {
    font-size: 16px;
    font-weight: 800;
    color: #e5e7eb;
    font-family: ui-monospace, monospace;
    font-variant-numeric: tabular-nums;
  }
  .sb-target {
    font-size: 11px;
    color: #ffb703;
    font-weight: 800;
    margin-top: 3px;
    background: rgba(251,191,36,0.10);
    border: 1px solid rgba(251,191,36,0.25);
    border-radius: 6px;
    padding: 2px 6px;
    display: inline-block;
  }
  .sb-target-sub {
    font-size: 9px;
    color: #9ca3af;
    font-weight: 700;
  }
  .sb-row2 {
    display: flex;
    gap: 6px;
  }
  .sb-card {
    flex: 1;
    background: rgba(255,255,255,0.04);
    border-radius: 8px;
    padding: 6px 8px;
    position: relative;
    overflow: hidden;
  }
  .accent {
    position: absolute;
    left: 0;
    top: 0;
    bottom: 0;
    width: 3px;
    border-radius: 3px 0 0 3px;
  }
  .accent-r {
    left: auto;
    right: 0;
    border-radius: 0 3px 3px 0;
  }
  .sb-line {
    display: flex;
    justify-content: space-between;
    align-items: center;
    padding: 2px 6px;
    font-size: 12px;
  }
  .sb-line.clickable {
    cursor: pointer;
    opacity: 0.8;
  }
  .sb-line.clickable:hover {
    opacity: 1;
    background: rgba(255,255,255,0.05);
    border-radius: 4px;
  }
  .pname {
    font-weight: 700;
    white-space: nowrap;
    overflow: hidden;
    text-overflow: ellipsis;
    max-width: 65%;
  }
  .pstats {
    font-weight: 600;
    color: #d1d5db;
    font-size: 11px;
    white-space: nowrap;
  }
  .muted {
    color: #6b7280;
    font-style: italic;
    font-size: 11px;
  }
  .dim {
    opacity: 0.6;
  }
  .lms {
    color: #fbbf24;
    font-size: 11px;
    font-weight: 800;
  }
  .over-strip {
    display: flex;
    gap: 3px;
    padding: 4px 6px 2px;
    flex-wrap: wrap;
  }
  .pill {
    display: inline-flex;
    align-items: center;
    justify-content: center;
    width: 22px;
    height: 22px;
    border-radius: 50%;
    font-size: 9px;
    font-weight: 800;
    color: #fff;
  }
  /* Only the freshest ball pops in — re-renders shouldn't replay every pill */
  .over-strip .pill:last-child { animation: pillIn 0.25s cubic-bezier(0.34, 1.5, 0.64, 1); }
  @keyframes pillIn { from { transform: scale(0.3); opacity: 0; } to { transform: scale(1); opacity: 1; } }
  .pill-dot { background: #374151; }
  .pill-run { background: #1d4ed8; }
  .pill-four { background: #059669; }
  .pill-six { background: #7c3aed; }
  .pill-wkt { background: #dc2626; }
  .pill-wd { background: #0891b2; font-size: 7px; }
  .pill-nb { background: #be185d; font-size: 7px; }
  .pill-empty { background: transparent; color: #4b5563; font-size: 12px; }
</style>
